/**
 * A facturação de uma obra, dentro da própria obra.
 *
 * Um ecrã à parte obrigava a procurar a obra outra vez e a confiar que se
 * tinha aberto a certa. Aqui emite-se ao lado do contrato que se está a ver.
 *
 * O botão de emitir mostra primeiro o que vai sair — destinatário, linhas e
 * total — porque um número de série gasta-se uma vez só: depois de emitido,
 * um engano corrige-se com nota de crédito, não com um segundo clique.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, FileText, AlertTriangle, Ban } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../hooks/useToast';

const ROTULO = {
  FATURA_CLIENTE: 'Factura ao cliente',
  FATURA_PRESTADOR: 'Factura da taxa ao executante',
  NOTA_CREDITO: 'Nota de crédito',
};

const kz = (v) => `${Number(v || 0).toLocaleString('pt-PT', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})} Kz`;
const dt = (v) => (v ? new Date(v).toLocaleString('pt-PT', { timeZone: 'Africa/Luanda' }) : '—');

const FaturacaoObra = ({ projectId, versao = 0 }) => {
  const { success, error } = useToast();
  const [dados, setDados] = useState(null);
  const [previa, setPrevia] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [aAnular, setAAnular] = useState(null);
  const [acerto, setAcerto] = useState(null);
  const [busy, setBusy] = useState(false);

  const carregar = useCallback(() => {
    api.get(`/projects/${projectId}/documentos`)
      .then((r) => setDados(r.data))
      .catch(() => setDados({ documentos: [], por_emitir: [] }));
    // `versao` sobe quando o perfil fiscal é gravado acima: o que estava
    // bloqueado por falta de NIF deixa de estar, e sem isto era preciso fechar
    // e reabrir a obra para o perceber.
  }, [projectId, versao]);
  useEffect(() => { carregar(); }, [carregar]);

  const prever = async (tipo) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/documentos/preparar/${tipo}`);
      setPrevia(data); setMotivo('');
    } catch (e) {
      error(e.response?.data?.message || 'Não foi possível preparar o documento.');
    } finally { setBusy(false); }
  };

  const emitir = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/documentos`, {
        tipo: previa.tipo,
        forcar: previa.bloqueios.length > 0,
        motivo: motivo.trim() || undefined,
      });
      success(`Emitido ${data.numero}.`);
      setPrevia(null); carregar();
    } catch (e) {
      error(e.response?.data?.message || 'Não foi possível emitir.');
    } finally { setBusy(false); }
  };

  const anular = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/projects/${projectId}/documentos/${aAnular.documento_id}/anular`,
        { motivo: motivo.trim() }
      );
      success(`Anulado pela nota de crédito ${data.numero}.`);
      setAAnular(null); setMotivo(''); carregar();
    } catch (e) {
      error(e.response?.data?.message || 'Não foi possível anular.');
    } finally { setBusy(false); }
  };

  const lancarAcerto = async () => {
    setBusy(true);
    try {
      // O sinal inverte-se aqui: a conciliação diz quanto ele deve, e o saldo
      // dele é que tem de descer.
      await api.post(`/withdrawals/conta/${acerto.worker_id}/acertos`, {
        business_id: acerto.business_id,
        valor_kz: -acerto.diferenca_kz,
        motivo: motivo.trim(),
        referencia: acerto.documento,
      });
      success(`Acerto lançado na conta de ${acerto.destinatario}.`);
      setAcerto(null); setMotivo(''); carregar();
    } catch (e) {
      error(e.response?.data?.message || 'Não foi possível lançar o acerto.');
    } finally { setBusy(false); }
  };

  const abrirPdf = (d) => {
    // O PDF vai por rota autenticada, por isso não se pode abrir o URL directo
    // num separador: sem o cabeçalho de autorização o servidor recusa. Busca-se
    // o ficheiro e abre-se a cópia local.
    api.get(`/projects/${projectId}/documentos/${d.documento_id}/pdf`, { responseType: 'blob' })
      .then((r) => window.open(URL.createObjectURL(r.data), '_blank'))
      .catch(() => error('Não foi possível abrir o documento.'));
  };

  if (!dados) {
    return <div className="pt-2 border-t border-slate-800 flex justify-center py-4">
      <Loader2 className="w-4 h-4 animate-spin text-slate-600" />
    </div>;
  }
  if (!dados.documentos.length && !dados.por_emitir.length) return null;

  return (
    <div className="pt-2 border-t border-slate-800 space-y-2">
      <div className="text-slate-300 font-semibold text-sm">Facturação</div>

      {dados.documentos.map((d) => (
        <div key={d.documento_id}
          className={`p-3 rounded-xl border text-sm ${d.anulado_por_documento_id
            ? 'bg-slate-950 border-slate-800 opacity-60' : 'bg-slate-950 border-slate-800'}`}>
          <div className="flex gap-3 items-start flex-wrap">
            <div className="flex-1 min-w-[180px]">
              <div className="font-mono text-xs text-slate-500">{d.numero}</div>
              <div className="text-slate-200">{ROTULO[d.tipo] || d.tipo}</div>
              <div className="text-xs text-slate-500">
                {d.destinatario_nome} · NIF {d.destinatario_nif || '—'} · {dt(d.emitido_em)}
              </div>
              {d.anulado_por_numero && (
                <div className="text-xs text-red-400 mt-1">Anulado por {d.anulado_por_numero}</div>
              )}
              {d.motivo && <div className="text-xs text-amber-500 mt-1">{d.motivo}</div>}
            </div>
            <div className="text-slate-100 font-semibold tabular-nums">{kz(d.total_kz)}</div>
          </div>
          <div className="flex gap-2 mt-2 flex-wrap">
            <button onClick={() => abrirPdf(d)}
              className="min-h-[40px] px-3 rounded-lg bg-slate-800 text-slate-300 text-sm inline-flex items-center gap-1.5">
              <FileText className="w-4 h-4" /> Abrir PDF
            </button>
            {!d.anulado_por_documento_id && d.tipo !== 'NOTA_CREDITO' && (
              <button onClick={() => { setAAnular(d); setMotivo(''); }}
                className="min-h-[40px] px-3 rounded-lg border border-slate-700 text-red-400 text-sm inline-flex items-center gap-1.5">
                <Ban className="w-4 h-4" /> Anular
              </button>
            )}
          </div>
        </div>
      ))}

      {/* O botão só convida quando há mesmo o que emitir. Estando a obra por
          assinar ou por pagar, mostra-se o motivo em vez do convite — os
          impedimentos vêm apurados do servidor, que é quem manda neles. */}
      {dados.por_emitir.map((p) => (p.bloqueios.length ? (
        <div key={p.tipo}
          className="w-full p-3 rounded-lg border border-dashed border-slate-800 text-sm">
          <div className="text-slate-400">{ROTULO[p.tipo]}</div>
          {p.bloqueios.map((b, i) => (
            <p key={i} className="text-xs text-amber-500 flex gap-1.5 mt-1">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" /> {b}
            </p>
          ))}
          {/* A saída de emergência fica, mas discreta: há obras fechadas antes
              de estes controlos existirem, que nunca mais chegam ao estado
              exigido. Quem passar por cima escreve o motivo e assina-o. */}
          <button type="button" onClick={() => prever(p.tipo)} disabled={busy}
            className="mt-2 text-xs text-slate-500 underline disabled:opacity-40">
            Emitir mesmo assim, com motivo
          </button>
        </div>
      ) : (
        <button key={p.tipo} onClick={() => prever(p.tipo)} disabled={busy}
          className="w-full min-h-[44px] rounded-lg border border-dashed border-slate-700 text-slate-300 text-sm disabled:opacity-40">
          Emitir {ROTULO[p.tipo].toLowerCase()} · {kz(p.total_kz)}
        </button>
      )))}

      {/* A conferência entre o que se facturou e o que se descontou. Só
          aparece quando os dois números discordam — quando batem certo não há
          nada a dizer, e uma linha a dizer "está tudo bem" é ruído. */}
      {dados.conciliacao && dados.conciliacao.diferenca_kz !== 0 && (
        <div className="p-3 rounded-xl bg-amber-950/30 border border-amber-900 text-sm space-y-1">
          <div className="text-amber-300 font-semibold flex gap-1.5">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> A factura e o desconto não batem
          </div>
          <div className="text-xs text-slate-400 tabular-nums">
            Facturado em {dados.conciliacao.documento}: {kz(dados.conciliacao.facturado_kz)}<br />
            Descontado nas etapas: {kz(dados.conciliacao.descontado_kz)}
          </div>
          <div className="text-slate-200">
            {dados.conciliacao.destinatario} deve {kz(Math.abs(dados.conciliacao.diferenca_kz))}
            {dados.conciliacao.diferenca_kz < 0 ? ' a receber' : ' à Dozy'}
          </div>
          <button onClick={() => { setAcerto(dados.conciliacao); setMotivo(''); }}
            className="mt-1 min-h-[40px] px-3 rounded-lg bg-amber-700 text-white text-sm font-semibold">
            Lançar acerto de conta
          </button>
        </div>
      )}

      {acerto && (
        <div className="fixed inset-0 z-[60] bg-black/70 p-4 overflow-y-auto"
          role="dialog" aria-modal="true" aria-label="Lançar acerto de conta">
          <div className="mx-auto w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-5 my-8 space-y-3">
            <h3 className="text-white font-bold">Acerto de conta</h3>
            <p className="text-sm text-slate-400">
              Desconta {kz(Math.abs(acerto.diferenca_kz))} do saldo de {acerto.destinatario}.
              As etapas já pagas não se tocam — fica um lançamento novo, datado e
              assinado, que a pessoa vê explicado na conta dela.
            </p>
            <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3}
              placeholder="Motivo do acerto"
              className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-sm" />
            <div className="flex gap-2">
              <button onClick={() => { setAcerto(null); setMotivo(''); }}
                className="flex-1 min-h-[44px] rounded-lg bg-slate-800 text-slate-300">Voltar</button>
              <button onClick={lancarAcerto} disabled={busy || !motivo.trim()}
                className="flex-1 min-h-[44px] rounded-lg bg-amber-700 text-white font-semibold disabled:opacity-40">
                Lançar
              </button>
            </div>
          </div>
        </div>
      )}

      {previa && (
        <div className="fixed inset-0 z-[60] bg-black/70 p-4 overflow-y-auto"
          role="dialog" aria-modal="true" aria-label="Confirmar emissão">
          <div className="mx-auto w-full max-w-lg rounded-2xl border border-slate-700 bg-slate-900 p-5 my-8 space-y-3">
            <h3 className="text-white font-bold">{ROTULO[previa.tipo]}</h3>
            <div className="text-sm text-slate-400">
              Para {previa.destinatario.nome} · NIF {previa.destinatario.nif || '—'}
            </div>

            <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 space-y-2">
              {previa.linhas.map((l, i) => (
                <div key={i} className="text-sm">
                  <div className="text-slate-300">{l.descricao}</div>
                  <div className="text-xs text-slate-500 tabular-nums">
                    Base {kz(l.base_kz)}
                    {Number(l.iva_kz) ? ` · IVA ${l.iva_pct}% ${kz(l.iva_kz)}` : ''}
                    {' · '}Total {kz(l.total_kz)}
                  </div>
                </div>
              ))}
              <div className="pt-2 border-t border-slate-800 flex justify-between text-slate-100 font-bold tabular-nums">
                <span>Total</span><span>{kz(previa.total_kz)}</span>
              </div>
            </div>

            {previa.avisos.map((a, i) => (
              <p key={i} className="text-xs text-amber-500 flex gap-1.5">
                <AlertTriangle className="w-4 h-4 shrink-0" /> {a}
              </p>
            ))}

            {previa.bloqueios.length > 0 && (
              <div className="p-3 rounded-xl bg-red-950/40 border border-red-900 space-y-2">
                <div className="text-sm text-red-300 font-semibold">Impedimentos</div>
                {previa.bloqueios.map((b, i) => <p key={i} className="text-xs text-red-300">{b}</p>)}
                <p className="text-xs text-slate-400">
                  Pode emitir na mesma, mas o motivo fica escrito no documento e no
                  registo de auditoria com o seu nome.
                </p>
                <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2}
                  placeholder="Porque é que se emite apesar disto"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-sm" />
              </div>
            )}

            <p className="text-xs text-slate-500">{previa.nota_legal}</p>

            <div className="flex gap-2">
              <button onClick={() => setPrevia(null)}
                className="flex-1 min-h-[44px] rounded-lg bg-slate-800 text-slate-300">Cancelar</button>
              <button onClick={emitir}
                disabled={busy || (previa.bloqueios.length > 0 && !motivo.trim())}
                className="flex-1 min-h-[44px] rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-40">
                Emitir
              </button>
            </div>
          </div>
        </div>
      )}

      {aAnular && (
        <div className="fixed inset-0 z-[60] bg-black/70 p-4 overflow-y-auto"
          role="dialog" aria-modal="true" aria-label="Anular documento">
          <div className="mx-auto w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-5 my-8 space-y-3">
            <h3 className="text-white font-bold">Anular {aAnular.numero}</h3>
            <p className="text-sm text-slate-400">
              O documento não desaparece: é emitida uma nota de crédito pelo valor
              simétrico, e ambos ficam no histórico. O motivo sai impresso.
            </p>
            <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3}
              placeholder="Motivo da anulação"
              className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-sm" />
            <div className="flex gap-2">
              <button onClick={() => { setAAnular(null); setMotivo(''); }}
                className="flex-1 min-h-[44px] rounded-lg bg-slate-800 text-slate-300">Voltar</button>
              <button onClick={anular} disabled={busy || !motivo.trim()}
                className="flex-1 min-h-[44px] rounded-lg bg-red-600 text-white font-semibold disabled:opacity-40">
                Anular
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FaturacaoObra;
